import type { MapEngine } from "../MapEngine";
import type { EngineClickEvent, QueriedFeature } from "../types";

/** Inspect mode for the manager (mirrors the state-layer InspectMode). */
export type InspectMode = "off" | "popup" | "panel";

/** Result delivered to the panel sink when inspecting in "panel" mode. */
export interface InspectResult {
    lngLat: { lng: number; lat: number };
    features: QueriedFeature[];
}

export interface InspectManagerOptions {
    /**
     * Called with queried features when mode is "panel". The viewer wires this
     * to the side-panel store. Popup mode renders inline and does not call this.
     */
    onPanelResult?: (result: InspectResult) => void;
    /**
     * Renders a popup at a location (popup mode). Injected so the manager does
     * not import maplibre directly; the engine layer supplies a popup renderer.
     */
    renderPopup?: (lngLat: { lng: number; lat: number }, features: QueriedFeature[]) => void;
}

/**
 * Subscribes to map clicks and, depending on the current mode, shows a popup
 * or pushes results to a panel. "off" disables inspection. Written once;
 * attached to a MapEngine. Call {@link dispose} to unsubscribe.
 */
export class InspectManager {
    private readonly engine: MapEngine;
    private readonly options: InspectManagerOptions;
    private mode: InspectMode = "off";
    private unsubscribe: (() => void) | null = null;

    constructor(engine: MapEngine, options: InspectManagerOptions = {}) {
        this.engine = engine;
        this.options = options;
    }

    /** Begins listening for clicks. Idempotent. */
    attach(): void {
        if (this.unsubscribe) return;
        this.unsubscribe = this.engine.on("click", this.handleClick);
    }

    setMode(mode: InspectMode): void {
        this.mode = mode;
    }

    getMode(): InspectMode {
        return this.mode;
    }

    /** Stops listening and releases the subscription. */
    dispose(): void {
        this.unsubscribe?.();
        this.unsubscribe = null;
    }

    private handleClick = (e: EngineClickEvent): void => {
        if (this.mode === "off") return;
        const features = this.engine.queryFeaturesAtPoint(e.point);
        if (features.length === 0) return;

        if (this.mode === "popup") {
            this.options.renderPopup?.(e.lngLat, features);
        } else if (this.mode === "panel") {
            this.options.onPanelResult?.({ lngLat: e.lngLat, features });
        }
    };
}
